import { AfterContentInit, Component, ElementRef, Input, OnDestroy, ViewChild, inject } from '@angular/core';
import { FitBoundsOptions, LngLat, LngLatBounds, Marker } from 'maplibre-gl';
import { GeolocationControl } from './geolocation-control';
import { MapService } from '@maplibre/ngx-maplibre-gl';
import { CallbackID, Geolocation, Position } from '@capacitor/geolocation';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';


/**
 * Source: https://github.com/maplibre/maplibre-gl-js/blob/main/src/ui/control/geolocate_control.ts
 */
@Component({
    selector: 'app-map-control-geolocation',
    template: `
    <div class="maplibregl-ctrl maplibregl-ctrl-group geolocation" #content>
      <span id="findMyLocation" style="display:none;">
        {{ 'map.geolocation.findMyLocation' | translate | translate }}
      </span>
      <button
        #geolocateButton
        type="button"
        class="maplibregl-ctrl-geolocate"
        [title]="'map.geolocation.findMyLocation' | translate"
        aria-labelledby="findMyLocation"
        [ariaPressed]="trackUserLocation"
        (click)="trigger()"
      >
        <span class="maplibregl-ctrl-icon" aria-hidden="true"></span>
      </button>
    </div>
  `,
    styleUrls: ['./geolocation.component.scss'],
    imports: [TranslatePipe]
})
export class GeolocationControlComponent implements AfterContentInit, OnDestroy {
  private mapService = inject(MapService);
  private translate = inject(TranslateService);

  @Input()
  public position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

  @Input()
  public trackUserLocation = false;

  @Input()
  public showUserLocation = true;

  @Input()
  public showAccuracyCircle = true;

  /**
   * A options object to use when the map is panned and zoomed to the user's location. The default is to use a `maxZoom` of 15 to limit how far the map will zoom in for very accurate locations.
   */
  @Input()
  public fitBoundsOptions: FitBoundsOptions = {
    maxZoom: 18
  };

  /**
   * A Geolocation API [PositionOptions](https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions) object.
   * @defaultValue `{enableHighAccuracy: false, timeout: 6000}`
   */
  @Input()
  public positionOptions: PositionOptions = {
    enableHighAccuracy: false,
    maximumAge: 0,
    timeout: 6000 /* 6 sec */
  };

  @ViewChild('content', { static: true }) 
  private content: ElementRef;

  private control: GeolocationControl;

  @ViewChild('geolocateButton', { static: true })
  private geolocateButton: ElementRef<HTMLButtonElement>;

  private geolocateButtonDotElement: HTMLDivElement;
  private geolocateButtonAccuracyCircleElement: HTMLDivElement;

  private mapLoaded = false;

  private watchState: 'OFF' | 'ACTIVE_LOCK' | 'WAITING_ACTIVE' | 'ACTIVE_ERROR' | 'BACKGROUND' | 'BACKGROUND_ERROR' = 'OFF';
  private watchCallbackID: CallbackID;
  private userLocationDotMarker: Marker;
  private accuracyCircleMarker: Marker;
  private lastKnownPosition: Position;
  private accuracy: number;

  constructor() {
    this.mapService.mapCreated$.subscribe(() => {
      this.mapLoaded = true;

      // when the camera is changed (and it's not as a result of the Geolocation Control) change
      // the watch mode to background watch, so that the marker is updated but not the camera.
      if (this.trackUserLocation) {
        this.mapService.mapInstance.on('movestart', (moveEvent: any) => {
          const fromResize = moveEvent.originalEvent && moveEvent.originalEvent.type === 'resize';
          if (!moveEvent.geolocateSource && this.watchState === 'ACTIVE_LOCK' && !fromResize) {
            this.watchState = 'BACKGROUND';
            this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-background');
            this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-active');
          }
        });
      }

      this.mapService.mapInstance.on('zoom', this.onZoom);
    });
    this.geolocateButtonDotElement = window.document.createElement('div');
    this.geolocateButtonDotElement.className = 'maplibregl-user-location-dot';
    this.userLocationDotMarker = new Marker({ element: this.geolocateButtonDotElement});

    this.geolocateButtonAccuracyCircleElement = window.document.createElement('div');
    this.geolocateButtonAccuracyCircleElement.className = 'maplibregl-user-location-accuracy-circle';
    this.accuracyCircleMarker = new Marker({ element: this.geolocateButtonAccuracyCircleElement, pitchAlignment: 'map' });
  }

  ngAfterContentInit(): void {
    if (this.content.nativeElement.childNodes.length) {
      this.control = new GeolocationControl(this.content.nativeElement);

      this.mapService.mapCreated$.subscribe(() => {
        this.mapService.addControl(this.control, this.position);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.mapService.mapInstance.hasControl(this.control)) {
      this.mapService.removeControl(this.control);
    }
  }

  public async trigger() {
    if(this.trackUserLocation) {
      switch(this.watchState) {
        case 'OFF':
          this.watchState = 'WAITING_ACTIVE';
          break;
        case 'WAITING_ACTIVE':
        case 'ACTIVE_LOCK':
        case 'ACTIVE_ERROR':
        case 'BACKGROUND_ERROR':
          this.watchState = 'OFF';

          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-waiting');
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-active');
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-active-error');
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-background');
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-background-error');
          break;
        case 'BACKGROUND':
          this.watchState = 'ACTIVE_LOCK';
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-background');

          if (this.lastKnownPosition) {
            this.updateCamera(this.lastKnownPosition);
          }
      }

      // incoming state setup
      switch (this.watchState) {
        case 'WAITING_ACTIVE':
          this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-waiting');
          this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-active');
          break;
        case 'ACTIVE_LOCK':
          this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-active');
          break;
        case 'OFF':
          break;
      }

      // manage geolocation.watchPosition / geolocation.clearWatch
      if (this.watchState === 'OFF' && this.watchCallbackID) {
        // clear watchPosition as we've changed to an OFF state
        this.clearWatch();
      } else if (!this.watchCallbackID) {
        // enable watchPosition since watchState is not OFF and there is no watchPosition already running

        this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-waiting');
        this.geolocateButton.nativeElement.setAttribute('aria-pressed', 'true');

        this.watchCallbackID = await Geolocation.watchPosition(this.positionOptions, (position: Position | null, error?: any) => {
          if(error) {
            this.onError(error);
          } else {
            this.onSuccess(position);
          }
        });
      }
    } else {
      Geolocation.getCurrentPosition(this.positionOptions)
        .then(this.onSuccess)
        .catch(this.onError);
    }
  }

  private onSuccess(position: Position) {
    if(!this.mapLoaded) {
      return ;
    }

    if (this.isOutOfMapMaxBounds(position)) {
      this.setErrorState();

      this.updateLocationMarker(null);
    }

    if (this.trackUserLocation) {
      // keep a record of the position so that if the state is BACKGROUND and the user
      // clicks the button, we can move to ACTIVE_LOCK immediately without waiting for
      // watchPosition to trigger onSuccess
      this.lastKnownPosition = position;

      switch (this.watchState) {
        case 'WAITING_ACTIVE':
        case 'ACTIVE_LOCK':
        case 'ACTIVE_ERROR':
          this.watchState = 'ACTIVE_LOCK';
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-waiting');
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-active-error');
          this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-active');
          break;
        case 'BACKGROUND':
        case 'BACKGROUND_ERROR':
          this.watchState = 'BACKGROUND';
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-waiting');
          this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-background-error');
          this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-background');
          break;
        default:
          throw new Error(`Unexpected watchState ${this.watchState}`);
      }
    }

    if (this.showUserLocation && this.watchState !== 'OFF') {
      this.updateLocationMarker(position);
    }

    if (!this.trackUserLocation || this.watchState === 'ACTIVE_LOCK') {
      this.updateCamera(position);
    }

    if (this.showUserLocation) {
      this.geolocateButtonDotElement.classList.remove('maplibregl-user-location-dot-stale');
    }
  }

  private async onError(error: any) {
    if (!this.mapService.mapInstance) {
      // control has since been removed
      return;
    }

    if(this.trackUserLocation) {
      const permission = await Geolocation.checkPermissions()

      if (error.code === 1 || permission.location === 'denied') {
        // PERMISSION_DENIED
        this.watchState = 'OFF';
        this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-waiting');
        this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-active');
        this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-active-error');
        this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-background');
        this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-background-error');
        this.geolocateButton.nativeElement.disabled = true;
        const title = this.translate.instant('map.geolocation.locationNotAvailable');
        this.geolocateButton.nativeElement.title = title;
        this.geolocateButton.nativeElement.setAttribute('aria-label', title);

        if (!this.watchCallbackID) {
          this.clearWatch();
        }
      } else {
        this.setErrorState();
      }
    }

    if(this.watchState !== 'OFF' && this.showUserLocation) {
      this.geolocateButtonDotElement.classList.add('maplibregl-user-location-dot-stale');
    }
  }

  private updateCamera(position: Position) {
    const center = new LngLat(position.coords.longitude, position.coords.latitude);
    const radius = position.coords.accuracy;
    const bearing = this.mapService.mapInstance.getBearing();
    const options: FitBoundsOptions = {
      ...this.fitBoundsOptions,
      bearing,
      zoom: this.mapService.mapInstance.getZoom()
    };
    const newBounds = LngLatBounds.fromLngLat(center, radius);

    this.mapService.mapInstance.fitBounds(newBounds, options, {
        geolocateSource: true // tag this camera change so it won't cause the control to change to background state
    });
  }

  private updateLocationMarker(position: Position | null) {
    if (position) {
      const center = new LngLat(position.coords.longitude, position.coords.latitude);
      this.accuracyCircleMarker.setLngLat(center).addTo(this.mapService.mapInstance);
      this.userLocationDotMarker.setLngLat(center).addTo(this.mapService.mapInstance);
      this.accuracy = position.coords.accuracy;
      if (this.showUserLocation && this.showAccuracyCircle) {
        this.updateCircleRadius();
      }
    } else {
      this.userLocationDotMarker.remove();
      this.accuracyCircleMarker.remove();
    }
  }

  private clearWatch() {
    Geolocation.clearWatch({ id: this.watchCallbackID });
    this.watchCallbackID = null;

    this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-waiting');
    this.geolocateButton.nativeElement.setAttribute('aria-pressed', 'false');

    if (this.showUserLocation) {
      this.updateLocationMarker(null);
    }
  }
  
  private updateCircleRadius() {
    const bounds = this.mapService.mapInstance.getBounds();
    const southEastPoint = bounds.getSouthEast();
    const northEastPoint = bounds.getNorthEast();
    const mapHeightInMeters = southEastPoint.distanceTo(northEastPoint);
    const mapHeightInPixels = this.mapService.mapInstance._container.clientHeight;
    const circleDiameter = Math.ceil(2 * (this.accuracy / (mapHeightInMeters / mapHeightInPixels)));
    this.geolocateButtonAccuracyCircleElement.style.width = `${circleDiameter}px`;
    this.geolocateButtonAccuracyCircleElement.style.height = `${circleDiameter}px`;
  }

  private onZoom() {
    if (this.showUserLocation && this.showAccuracyCircle) {
      this.updateCircleRadius();
    }
  }

  private onRemove() {
    // clear the geolocation watch if exists
    if (!this.watchCallbackID) {
      Geolocation.clearWatch({ id: this.watchCallbackID });
      this.watchCallbackID = null;
    }

    // clear the markers from the map
    if (this.showUserLocation && this.userLocationDotMarker) {
      this.userLocationDotMarker.remove();
    }
    if (this.showAccuracyCircle && this.accuracyCircleMarker) {
      this.accuracyCircleMarker.remove();
    }

    this.control.onRemove(this.mapService.mapInstance)
    this.mapService.mapInstance.off('zoom', this.onZoom);
  }

  private isOutOfMapMaxBounds(position: Position) {
    const bounds = this.mapService.mapInstance.getMaxBounds();
    const coordinates = position.coords;

    return bounds && (
      coordinates.longitude < bounds.getWest() ||
      coordinates.longitude > bounds.getEast() ||
      coordinates.latitude < bounds.getSouth() ||
      coordinates.latitude > bounds.getNorth()
    );
  }

  private setErrorState() {
    switch (this.watchState) {
      case 'WAITING_ACTIVE':
        this.watchState = 'ACTIVE_ERROR';
        this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-active');
        this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-active-error');
        break;
      case 'ACTIVE_LOCK':
        this.watchState = 'ACTIVE_ERROR';
        this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-active');
        this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-active-error');
        this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-waiting');
        // turn marker grey
        break;
      case 'BACKGROUND':
        this.watchState = 'BACKGROUND_ERROR';
        this.geolocateButton.nativeElement.classList.remove('maplibregl-ctrl-geolocate-background');
        this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-background-error');
        this.geolocateButton.nativeElement.classList.add('maplibregl-ctrl-geolocate-waiting');
        // turn marker grey
        break;
      case 'ACTIVE_ERROR':
        break;
      default:
        throw new Error(`Unexpected watchState ${this.watchState}`);
    }
  }
}
